/**
 * Time validation utilities for itinerary waypoints
 * Validates that arrival times are realistic based on distance from previous waypoint
 */

import { WaypointResponse } from '@/shared/api/routes'
import { calculateMinimumTravelTime, WaypointDistance } from './itineraryDistanceUtils'
import { formatTimeForDisplay } from './timeCalculations'

export interface TimeValidationWarning {
  hasWarning: boolean
  message: string | null
  minimumTravelTimeMinutes: number
  actualTimeAvailableMinutes: number | null
}

/**
 * Parse time string (HH:MM format) to minutes since midnight
 */
function timeToMinutes(timeString: string | null | undefined): number | null {
  if (!timeString) return null

  // Handle both HH:MM and HHMM formats
  const normalized = timeString.replace(':', '')
  if (normalized.length !== 4) return null

  const hours = parseInt(normalized.substring(0, 2), 10)
  const minutes = parseInt(normalized.substring(2, 4), 10)

  if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return null
  }

  return hours * 60 + minutes
}

/**
 * Calculate time difference in minutes between two times
 * Handles wrap-around (e.g., 23:00 to 01:00 = 2 hours = 120 minutes)
 */
function timeDifferenceMinutes(time1: number | null, time2: number | null): number | null {
  if (time1 === null || time2 === null) return null

  // If time2 is earlier than time1, assume it's the next day
  if (time2 < time1) {
    return (24 * 60 - time1) + time2
  }

  return time2 - time1
}

/**
 * Validate arrival time for a waypoint based on distance from previous waypoint
 */
export function validateWaypointArrivalTime(
  waypoint: WaypointResponse,
  previousWaypoint: WaypointResponse | null,
  distance: WaypointDistance,
  averageSpeedKmh: number = 50
): TimeValidationWarning {
  // No previous waypoint - no validation needed
  if (!previousWaypoint) {
    return {
      hasWarning: false,
      message: null,
      minimumTravelTimeMinutes: 0,
      actualTimeAvailableMinutes: null
    }
  }

  // No arrival time set - no validation needed
  const arrivalTime = waypoint.start_time || waypoint.end_time
  if (!arrivalTime) {
    return {
      hasWarning: false,
      message: null,
      minimumTravelTimeMinutes: calculateMinimumTravelTime(distance.distanceMeters, averageSpeedKmh),
      actualTimeAvailableMinutes: null
    }
  }

  // Get departure time from previous waypoint
  const previousDepartureTime = previousWaypoint.end_time || previousWaypoint.start_time
  if (!previousDepartureTime) {
    return {
      hasWarning: false,
      message: null,
      minimumTravelTimeMinutes: calculateMinimumTravelTime(distance.distanceMeters, averageSpeedKmh),
      actualTimeAvailableMinutes: null
    }
  }

  // Calculate minimum required travel time
  const minimumTravelTimeMinutes = calculateMinimumTravelTime(distance.distanceMeters, averageSpeedKmh)

  // Convert times to minutes
  const arrivalMinutes = timeToMinutes(arrivalTime)
  const departureMinutes = timeToMinutes(previousDepartureTime)

  if (arrivalMinutes === null || departureMinutes === null) {
    return {
      hasWarning: false,
      message: null,
      minimumTravelTimeMinutes,
      actualTimeAvailableMinutes: null
    }
  }

  // Calculate actual time available
  const actualTimeAvailableMinutes = timeDifferenceMinutes(departureMinutes, arrivalMinutes)

  if (actualTimeAvailableMinutes === null) {
    return {
      hasWarning: false,
      message: null,
      minimumTravelTimeMinutes,
      actualTimeAvailableMinutes: null
    }
  }

  // Check if there's enough time
  if (actualTimeAvailableMinutes < minimumTravelTimeMinutes) {
    const arrivalDisplay = formatTimeForDisplay(arrivalTime)
    const departureDisplay = formatTimeForDisplay(previousDepartureTime)
    const shortfall = minimumTravelTimeMinutes - actualTimeAvailableMinutes

    return {
      hasWarning: true,
      message: `Arrival time may be unrealistic. Minimum travel time is ${minimumTravelTimeMinutes} minutes (${distance.distanceFormatted} at ${averageSpeedKmh} km/h), but only ${actualTimeAvailableMinutes} minutes available between ${departureDisplay} and ${arrivalDisplay}.`,
      minimumTravelTimeMinutes,
      actualTimeAvailableMinutes
    }
  }

  return {
    hasWarning: false,
    message: null,
    minimumTravelTimeMinutes,
    actualTimeAvailableMinutes
  }
}

